import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Col,
  Row,
  Button,
  Form,
  FormGroup,
  Spinner,
  Card,
} from "react-bootstrap";
import { toast } from "react-toastify";
import api from "common/api";
import * as formik from "formik";
import * as yup from "yup";
import AlreadyPaid from "./AlreadyPaid/AlreadyPaid";

function OrderPayments() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [order, setOrder] = useState<any>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [countries, setCountries] = useState<{ id: number; name: string }[]>(
    [],
  );
  const [initialValues, setInitialValues] = useState({
    fullName: "",
    address: "",
    city: "",
    zipcode: "",
    country: "",
    phone: "",
    email: "",
    termsAccepted: false,
  });
  const { Formik } = formik;

  const schema = yup.object({
    fullName: yup
      .string()
      .required("Potrebno je unijeti ime platitelja")
      .min(3, "Ime mora imati najmanje 3 znaka"),
    address: yup
      .string()
      .required("Potrebno je unijeti adresu platitelja")
      .min(3, "Adresa mora imati najmanje 3 znaka"),
    city: yup
      .string()
      .required("Potrebno je unijeti grad platitelja")
      .min(3, "Grad mora imati najmanje 3 znaka"),
    zipcode: yup
      .string()
      .required("Potrebno je unijeti poštanski broj platitelja")
      .min(3, "Poštanski broj mora imati najmanje 3 znaka"),
    country: yup.string().required("Potrebno je unijeti državu platitelja"),
    phone: yup
      .string()
      .required("Potrebno je unijeti kontakt broj")
      .min(3, "Kontakt broj mora imati najmanje 3 znaka"),
    email: yup
      .string()
      .email("Unesite ispravnu email adresu")
      .required("Potrebno je unijeti email adresu"),
    termsAccepted: yup
      .boolean()
      .oneOf([true], "Morate prihvatiti uvjete kupnje"),
  });

  useEffect(() => {
    const fetchOrders = async () => {
      setLoading(true);
      try {
        const response = await api.get(`/orders/${id}/`);
        const order = response.data;
        setOrder(order);
      } catch (error) {
        console.error(error);
        toast.error("Greška prilikom učitavanja narudžbe");
      } finally {
        setLoading(false);
      }
    };

    const fetchCountries = async () => {
      try {
        const response = await api.get(`/countries/`);
        if (Array.isArray(response.data)) {
          setCountries(response.data);
          const hrvatska = response.data.find(
            (country) => country.name === "Hrvatska",
          );
          if (hrvatska) {
            setInitialValues((prev) => ({
              ...prev,
              country: String(hrvatska.id),
            }));
          }
        } else {
          console.error("Unexpected response format:", response.data);
          toast.error("Neispravan format podataka za države");
        }
      } catch (error) {
        console.error(error);
        toast.error("Greška prilikom učitavanja država");
      }
    };

    fetchOrders();
    fetchCountries();
  }, [id]);

  const handleSubmit = async (formData: any, { setSubmitting }: any) => {
    const requestData = {
      order_id: id,
      full_name: formData.fullName,
      address: formData.address,
      city: formData.city,
      zipcode: formData.zipcode,
      country: formData.country,
      phone: formData.phone,
      email: formData.email,
    };

    try {
      const response = await api.post(`/orders/${id}/pay/`, requestData);
      const { payment_form_url } = response.data;
      window.open(payment_form_url, "_blank");
      navigate("/placanje-u-tijeku");
    } catch (error) {
      console.log(error);
      toast.error("Greška prilikom slanja podataka za plaćanje");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div
        className="d-flex justify-content-center align-items-center"
        style={{ height: "50vh" }}
      >
        <Spinner animation="border" />
      </div>
    );
  }

  if (!order) {
    return <div className="text-center mt-5">Narudžba nije pronađena</div>;
  }

  if (order.paid) {
    return <AlreadyPaid />;
  }

  return (
    <div>
      <Row className="mt-4">
        <Col>
          <h4 className="mb-4">Plaćanje priprema za državnu maturu</h4>
          <Formik
            validationSchema={schema}
            onSubmit={handleSubmit}
            enableReinitialize={true}
            initialValues={initialValues}
          >
            {({
              handleSubmit,
              handleChange,
              isSubmitting,
              handleBlur,
              values,
              touched,
              errors,
            }) => (
              <Form noValidate onSubmit={handleSubmit}>
                {/* Form Fields */}
                <Row>
                  <FormGroup
                    as={Col}
                    lg={6}
                    className="mb-3"
                    controlId="formFullName"
                  >
                    <Form.Label>Ime i prezime platitelja</Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="Unesite puno ime"
                      name="fullName"
                      value={values.fullName}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      isInvalid={touched.fullName && !!errors.fullName}
                    />
                    <Form.Control.Feedback type="invalid">
                      {errors.fullName}
                    </Form.Control.Feedback>
                  </FormGroup>

                  <FormGroup
                    as={Col}
                    lg={6}
                    className="mb-3"
                    controlId="formAddress"
                  >
                    <Form.Label>Adresa</Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="Unesite adresu"
                      name="address"
                      value={values.address}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      isInvalid={touched.address && !!errors.address}
                    />
                    <Form.Control.Feedback type="invalid">
                      {errors.address}
                    </Form.Control.Feedback>
                  </FormGroup>
                </Row>

                <Row>
                  <FormGroup
                    as={Col}
                    lg={6}
                    className="mb-3"
                    controlId="formCity"
                  >
                    <Form.Label>Grad</Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="Unesite grad"
                      name="city"
                      value={values.city}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      isInvalid={touched.city && !!errors.city}
                    />
                    <Form.Control.Feedback type="invalid">
                      {errors.city}
                    </Form.Control.Feedback>
                  </FormGroup>

                  <FormGroup
                    as={Col}
                    lg={6}
                    className="mb-3"
                    controlId="formZipcode"
                  >
                    <Form.Label>Poštanski broj</Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="Unesite poštanski broj"
                      name="zipcode"
                      value={values.zipcode}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      isInvalid={touched.zipcode && !!errors.zipcode}
                    />
                    <Form.Control.Feedback type="invalid">
                      {errors.zipcode}
                    </Form.Control.Feedback>
                  </FormGroup>
                </Row>

                <Row>
                  <FormGroup
                    as={Col}
                    lg={6}
                    className="mb-3"
                    controlId="formCountry"
                  >
                    <Form.Label>Država</Form.Label>
                    <Form.Control
                      as="select"
                      name="country"
                      value={values.country}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      isInvalid={touched.country && !!errors.country}
                    >
                      <option value="">Odaberite državu</option>
                      {countries.map((country) => (
                        <option key={country.id} value={country.id}>
                          {country.name}
                        </option>
                      ))}
                    </Form.Control>
                    <Form.Control.Feedback type="invalid">
                      {errors.country}
                    </Form.Control.Feedback>
                  </FormGroup>

                  <FormGroup
                    as={Col}
                    lg={6}
                    className="mb-3"
                    controlId="formPhone"
                  >
                    <Form.Label>Kontakt broj</Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="Unesite kontakt broj"
                      name="phone"
                      value={values.phone}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      isInvalid={touched.phone && !!errors.phone}
                    />
                    <Form.Control.Feedback type="invalid">
                      {errors.phone}
                    </Form.Control.Feedback>
                  </FormGroup>
                </Row>

                <Row>
                  <Col lg={6}>
                    {/* Email Field */}
                    <FormGroup className="mb-3" controlId="formEmail">
                      <Form.Label>Email</Form.Label>
                      <Form.Control
                        type="email"
                        placeholder="Unesite email adresu"
                        name="email"
                        value={values.email}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        isInvalid={touched.email && !!errors.email}
                      />
                      <Form.Control.Feedback type="invalid">
                        {errors.email}
                      </Form.Control.Feedback>
                    </FormGroup>

                    <FormGroup className="mb-3" controlId="formTerms">
                      <Form.Check
                        type="checkbox"
                        label={
                          <>
                            Slažem se s uvjetima kupnje opisane u{" "}
                            <a
                              href="/opci-uvjeti"
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              Općim uvjetima
                            </a>
                          </>
                        }
                        name="termsAccepted"
                        checked={values.termsAccepted}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        isInvalid={
                          touched.termsAccepted && !!errors.termsAccepted
                        }
                        feedback={errors.termsAccepted}
                      />
                    </FormGroup>
                  </Col>

                  <Col lg={6}>
                    {/* Summary Card */}
                    <Card className="mb-3">
                      <Card.Body>
                        <p>
                          PDV (25%): {(+order.amount * 0.25).toFixed(2)} EUR
                        </p>
                        <strong>Ukupni iznos: {order.amount} EUR</strong>
                      </Card.Body>
                    </Card>

                    <Button
                      variant="primary"
                      type="submit"
                      className="float-end"
                      disabled={isSubmitting || !values.termsAccepted}
                    >
                      {isSubmitting && (
                        <span style={{ paddingRight: "10px" }}>
                          <Spinner size="sm" />
                        </span>
                      )}
                      <span>Nastavi na plaćanje</span>
                    </Button>
                  </Col>
                </Row>
              </Form>
            )}
          </Formik>
        </Col>
      </Row>
    </div>
  );
}

export default OrderPayments;
